import { type RoleListItem } from '@utazzo/shared';
import { UzButton } from '@utazzo/ui';
import {
  ChevronDown,
  ChevronRight,
  Plus,
  ShieldCheck,
  Trash2,
  Users as UsersIcon,
} from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { PermissionMatrix } from '../components/PermissionMatrix';
import { RoleFormDrawer } from '../components/RoleFormDrawer';
import { UserPermissionsPanel } from '../components/UserPermissionsPanel';
import { useDeleteRole, usePermissionRegistry, useRoleDetail, useRolesList } from '../use-roles';

interface TreeNode {
  role: RoleListItem;
  children: TreeNode[];
  depth: number;
}

function buildTree(roles: ReadonlyArray<RoleListItem>): TreeNode[] {
  const byId = new Map<string, TreeNode>();
  for (const r of roles) byId.set(r.id, { role: r, children: [], depth: 0 });
  const roots: TreeNode[] = [];
  for (const node of byId.values()) {
    if (node.role.parentRoleId && byId.has(node.role.parentRoleId)) {
      const parent = byId.get(node.role.parentRoleId)!;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const assignDepth = (n: TreeNode, d: number): void => {
    n.depth = d;
    for (const c of n.children) assignDepth(c, d + 1);
  };
  for (const r of roots) assignDepth(r, 0);
  return roots;
}

function flatten(nodes: TreeNode[], expanded: Set<string>): TreeNode[] {
  const out: TreeNode[] = [];
  const visit = (n: TreeNode): void => {
    out.push(n);
    if (expanded.has(n.role.id)) {
      for (const c of n.children) visit(c);
    }
  };
  for (const r of nodes) visit(r);
  return out;
}

interface RowProps {
  node: TreeNode;
  isExpanded: boolean;
  isSelected: boolean;
  onToggle: () => void;
  onSelect: () => void;
}

const RoleRow = ({
  node,
  isExpanded,
  isSelected,
  onToggle,
  onSelect,
}: RowProps): React.JSX.Element => {
  const hasChildren = node.children.length > 0;
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`flex w-full items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors ${
        isSelected ? 'bg-zinc-100 ring-1 ring-zinc-300' : 'hover:bg-zinc-50'
      }`}
      style={{ paddingLeft: `${0.5 + node.depth * 1.25}rem` }}
    >
      {hasChildren ? (
        <span
          role="button"
          tabIndex={0}
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onToggle();
            }
          }}
          className="rounded p-0.5 text-zinc-500 hover:bg-zinc-200"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </span>
      ) : (
        <span className="size-5" aria-hidden />
      )}
      <ShieldCheck className="size-4 text-zinc-400" aria-hidden />
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium text-zinc-900">{node.role.name}</span>
        <span className="truncate text-xs text-zinc-500">{node.role.code}</span>
      </div>
      <div className="flex items-center gap-3 text-xs text-zinc-500">
        <span title="Users assigned" className="inline-flex items-center gap-1">
          <UsersIcon className="size-3.5" aria-hidden />
          {node.role.userCount}
        </span>
        <span title="Permissions granted">{node.role.permissionCount} perms</span>
      </div>
    </button>
  );
};

interface DetailProps {
  roleId: string;
  allRoles: ReadonlyArray<RoleListItem>;
}

const RoleDetailPanel = ({ roleId, allRoles }: DetailProps): React.JSX.Element => {
  const detail = useRoleDetail(roleId);
  const registry = usePermissionRegistry();
  const deleteRole = useDeleteRole();
  const [editOpen, setEditOpen] = useState(false);

  if (detail.isLoading) {
    return <div className="p-6 text-sm text-zinc-500">Loading role detail...</div>;
  }
  if (detail.error || !detail.data) {
    return <div className="p-6 text-sm text-red-600">Could not load role detail.</div>;
  }
  const r = detail.data;
  const inheritedSet = new Set<string>();
  const canDelete = !r.isSystem && r.userCount === 0;

  const handleDelete = async (): Promise<void> => {
    const ok = window.confirm(
      `Delete role "${r.name}"? It will be soft-deleted and hidden from selects. This cannot be undone from the UI.`,
    );
    if (!ok) return;
    await deleteRole.mutateAsync(r.id);
  };

  return (
    <div className="flex flex-col gap-6 p-6">
      <header className="flex flex-col gap-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-zinc-900">{r.name}</h2>
            <p className="mt-1 text-xs text-zinc-500">
              code: <span className="font-mono">{r.code}</span>
              {r.parentRoleCode && (
                <>
                  <span className="mx-2 text-zinc-300">|</span>
                  inherits from <span className="font-mono">{r.parentRoleCode}</span>
                </>
              )}
              {r.isSystem && (
                <>
                  <span className="mx-2 text-zinc-300">|</span>
                  <span className="rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium tracking-wider text-blue-700 uppercase">
                    system
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <UzButton variant="ghost" onClick={() => setEditOpen(true)}>
              Edit
            </UzButton>
            <UzButton
              variant="ghost"
              onClick={handleDelete}
              disabled={!canDelete || deleteRole.isPending}
              title={
                r.isSystem
                  ? 'System role - cannot be deleted'
                  : r.userCount > 0
                    ? `Reassign ${r.userCount} user(s) first`
                    : undefined
              }
            >
              <Trash2 className="size-4" aria-hidden />
            </UzButton>
          </div>
        </div>
        {r.description && <p className="text-sm text-zinc-700">{r.description}</p>}
      </header>

      <section className="grid grid-cols-1 gap-3 rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3 sm:grid-cols-2">
        <div>
          <div className="text-xs text-zinc-500">Users</div>
          <div className="mt-1 text-lg font-semibold text-zinc-900">{r.userCount}</div>
        </div>
        <div>
          <div className="text-xs text-zinc-500">Permission grants</div>
          <div className="mt-1 text-lg font-semibold text-zinc-900">{r.permissions.length}</div>
        </div>
      </section>

      {registry.data ? (
        <PermissionMatrix
          role={r}
          registry={registry.data.modules}
          groups={registry.data.groups}
          inheritedSet={inheritedSet}
        />
      ) : (
        <div className="rounded-md border border-zinc-200 px-4 py-6 text-sm text-zinc-500">
          Loading permission catalog...
        </div>
      )}

      <RoleFormDrawer
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        allRoles={allRoles}
        role={r}
      />
    </div>
  );
};

type RolesPageTab = 'roles' | 'user-permissions';

export const RolesListPage = (): React.JSX.Element => {
  const list = useRolesList({ page: 1, pageSize: 100, sortDir: 'asc' });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // 2026-06-23: the page now hosts two tabs:
  //   - 'roles'            : the existing role tree + permission matrix
  //                          (default landing).
  //   - 'user-permissions' : the per-user EXTRA grants picker, where an
  //                          admin can grant additional permissions to a
  //                          specific user on top of their role baseline.
  // The previous "Modules" surface was removed - the matrix already
  // shows every module per role, so a separate list was redundant.
  const [tab, setTab] = useState<RolesPageTab>('roles');

  const tree = useMemo(() => buildTree(list.data?.data ?? []), [list.data]);
  const visible = useMemo(() => flatten(tree, expanded), [tree, expanded]);
  const allRoles = list.data?.data ?? [];

  React.useEffect(() => {
    const first = tree[0];
    if (!selectedId && first) {
      const roots = new Set(tree.map((n) => n.role.id));
      setExpanded(roots);
      setSelectedId(first.role.id);
    }
  }, [tree, selectedId]);

  const toggle = (id: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (list.isLoading) {
    return <div className="p-6 text-sm text-zinc-500">Loading roles...</div>;
  }
  if (list.error) {
    return <div className="p-6 text-sm text-red-600">Could not load roles. Try refreshing.</div>;
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-start justify-between border-b border-zinc-200 px-6 py-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900">Access control</h1>
          <p className="mt-1 text-sm text-zinc-600">
            Role hierarchy + per-user grants. Roles set the baseline; the User permissions tab
            grants extras on top for individual users.
          </p>
        </div>
        {tab === 'roles' && (
          <UzButton onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" aria-hidden />
            New role
          </UzButton>
        )}
      </header>
      <div className="border-b border-zinc-200 px-6">
        <div className="flex gap-2">
          {(
            [
              { id: 'roles', label: 'Roles' },
              { id: 'user-permissions', label: 'User permissions' },
            ] as ReadonlyArray<{ id: RolesPageTab; label: string }>
          ).map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                className={
                  'border-b-2 px-3 py-2 text-sm font-medium transition-colors ' +
                  (active
                    ? 'border-indigo-500 text-indigo-700'
                    : 'border-transparent text-zinc-600 hover:text-zinc-900')
                }
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>
      {tab === 'roles' ? (
        // Below lg the two panes stack (role list on top, detail beneath) and
        // the whole column scrolls; the desktop split is unchanged.
        <div className="flex flex-1 flex-col overflow-y-auto lg:grid lg:grid-cols-[minmax(260px,360px)_1fr] lg:overflow-hidden">
          <aside className="border-b border-zinc-200 p-3 lg:max-h-none lg:overflow-y-auto lg:border-r lg:border-b-0">
            <div className="flex flex-col gap-0.5">
              {visible.map((n) => (
                <RoleRow
                  key={n.role.id}
                  node={n}
                  isExpanded={expanded.has(n.role.id)}
                  isSelected={selectedId === n.role.id}
                  onToggle={() => toggle(n.role.id)}
                  onSelect={() => setSelectedId(n.role.id)}
                />
              ))}
            </div>
          </aside>
          <main className="min-w-0 lg:overflow-y-auto">
            {selectedId ? (
              <RoleDetailPanel roleId={selectedId} allRoles={allRoles} />
            ) : (
              <div className="p-6 text-sm text-zinc-500">Select a role to inspect.</div>
            )}
          </main>
        </div>
      ) : (
        <UserPermissionsPanel />
      )}

      <RoleFormDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        mode="create"
        allRoles={allRoles}
      />
    </div>
  );
};
